import React, { useMemo } from 'react';
import { Bell, Check, CheckCheck, CircleDot, CalendarClock, AlertTriangle, Inbox } from 'lucide-react';
import { Link } from 'wouter';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useNotifications } from '@/contexts/NotificationsContext';
import type { TaskNotification, TaskNotificationStatus } from '@/lib/api';
import { cn } from '@/lib/utils';

const STATUS_META: Record<
  TaskNotificationStatus,
  { label: string; color: string; order: number; Icon: React.ComponentType<{ className?: string }> }
> = {
  behind: { label: 'Просрочено', color: '#E85D04', order: 0, Icon: AlertTriangle },
  active: { label: 'В работе', color: '#00A651', order: 1, Icon: CircleDot },
  planned: { label: 'В плане', color: '#0055A5', order: 2, Icon: CalendarClock },
  backlog: { label: 'Бэклог', color: '#7C3AED', order: 3, Icon: Inbox },
  done: { label: 'Выполнено', color: '#8B9BB4', order: 4, Icon: Check },
};

function formatDateRu(iso: string | null): string {
  if (!iso) return '';
  try {
    const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
    return d.toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' });
  } catch {
    return '';
  }
}

const KIND_META: Record<'gantt' | 'backlog', { label: string; bg: string; color: string }> = {
  gantt: { label: 'Гант', bg: 'rgba(0, 85, 165, 0.12)', color: '#0055A5' },
  backlog: { label: 'Канбан', bg: 'rgba(139, 92, 246, 0.14)', color: '#7C3AED' },
};

function buildDeepLink(n: TaskNotification): string {
  const kind = n.taskKind ?? 'gantt';
  const param = kind === 'backlog' ? 'card' : 'task';
  return `/project/${n.projectId}?${param}=${encodeURIComponent(n.taskId)}`;
}

function NotificationItem({
  n,
  onRead,
}: {
  n: TaskNotification;
  onRead: () => void;
}) {
  const meta = STATUS_META[n.status];
  const Icon = meta.Icon;
  const end = formatDateRu(n.end);
  const kindMeta = KIND_META[n.taskKind ?? 'gantt'];
  return (
    <Link href={buildDeepLink(n)}>
      <div
        role="button"
        tabIndex={0}
        onClick={onRead}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onRead();
        }}
        className={cn(
          'group relative flex cursor-pointer gap-2 rounded-md px-2 py-2 transition-colors hover:bg-muted/60',
          !n.isRead && !n.isResolved && 'bg-[var(--nn-blue-pale)]/30',
        )}
      >
        <span
          aria-hidden
          className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: `${meta.color}22`, color: meta.color }}
        >
          <Icon className="size-3" />
        </span>
        <div className="min-w-0 flex-1 leading-tight">
          <div className="flex flex-wrap items-center gap-1.5">
            <span
              className="rounded-full px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide"
              style={{ backgroundColor: `${meta.color}22`, color: meta.color }}
            >
              {meta.label}
            </span>
            <span
              className="rounded-full px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide"
              style={{ backgroundColor: kindMeta.bg, color: kindMeta.color }}
              title={kindMeta.label === 'Гант' ? 'Задача на диаграмме Ганта' : 'Карточка на канбан-доске'}
            >
              {kindMeta.label}
            </span>
            {!n.isRead && !n.isResolved ? (
              <span className="inline-block size-1.5 rounded-full bg-[var(--nn-blue)]" aria-label="Непрочитано" />
            ) : null}
          </div>
          <div className="mt-0.5 line-clamp-2 text-[13px] font-medium">{n.taskTitle}</div>
          <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
            {n.projectTitle}
            {end ? ` · до ${end}` : ''}
          </div>
        </div>
      </div>
    </Link>
  );
}

export function NotificationBell({
  collapsed = false,
}: {
  collapsed?: boolean;
}) {
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications();

  const active = useMemo(
    () =>
      notifications
        .filter((n) => !n.isResolved && n.status !== 'done')
        .sort(
          (a, b) => STATUS_META[a.status].order - STATUS_META[b.status].order ||
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
        )
        .slice(0, 12),
    [notifications],
  );

  const grouped = useMemo(() => {
    const byStatus: Record<TaskNotificationStatus, TaskNotification[]> = {
      behind: [],
      active: [],
      planned: [],
      backlog: [],
      done: [],
    };
    for (const n of active) byStatus[n.status].push(n);
    return byStatus;
  }, [active]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'relative flex items-center gap-2 rounded-md border border-white/10 px-2 py-1.5 text-[12px] font-medium transition-colors hover:bg-white/10',
            collapsed && 'w-full justify-center px-0',
          )}
          style={{ color: 'oklch(0.78 0.04 240)' }}
          title={collapsed ? 'Мои задачи' : undefined}
          aria-label={`Мои задачи${unreadCount > 0 ? ` (${unreadCount} новых)` : ''}`}
        >
          <span className="relative inline-flex">
            <Bell className="size-4" strokeWidth={2} />
            {unreadCount > 0 ? (
              <span
                className="absolute -right-1.5 -top-1.5 inline-flex h-[14px] min-w-[14px] items-center justify-center rounded-full bg-rose-500 px-[3px] text-[9px] font-bold leading-none text-white ring-2 ring-[var(--sidebar)]"
                aria-hidden
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            ) : null}
          </span>
          {!collapsed ? <span className="truncate">Мои задачи</span> : null}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="right"
        sideOffset={10}
        className="z-[60] w-[min(22rem,calc(100vw-1.5rem))] p-0 shadow-xl"
      >
        <div className="flex items-center justify-between border-b px-3 py-2">
          <div className="text-sm font-semibold">Уведомления</div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => markAllRead()}
              disabled={unreadCount === 0}
              className={cn(
                'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] transition-colors',
                unreadCount === 0
                  ? 'text-muted-foreground/50'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              )}
              title="Пометить все как прочитанные"
            >
              <CheckCheck className="size-3" />
              Прочитать всё
            </button>
          </div>
        </div>
        <div className="max-h-[min(70dvh,28rem)] overflow-y-auto overscroll-contain p-1.5">
          {active.length === 0 ? (
            <div className="flex flex-col items-center gap-1 px-3 py-8 text-center text-[12px] text-muted-foreground">
              <Bell className="size-5 opacity-60" />
              Нет задач, назначенных на вас.
            </div>
          ) : (
            (['behind', 'active', 'planned', 'backlog'] as const).map((status) =>
              grouped[status].length === 0 ? null : (
                <div key={status} className="mb-1">
                  <div
                    className="sticky top-0 z-[1] bg-popover/95 px-1.5 py-1 text-[10px] font-semibold uppercase tracking-wider"
                    style={{ color: STATUS_META[status].color }}
                  >
                    {STATUS_META[status].label} · {grouped[status].length}
                  </div>
                  <div className="flex flex-col gap-0.5">
                    {grouped[status].map((n) => (
                      <NotificationItem key={n.id} n={n} onRead={() => markRead(n.id)} />
                    ))}
                  </div>
                </div>
              ),
            )
          )}
        </div>
        <div className="border-t px-2 py-1.5 text-center">
          <Link href="/me/tasks">
            <span className="inline-block w-full rounded px-2 py-1 text-[12px] font-medium text-[var(--nn-blue)] hover:bg-[var(--nn-blue-pale)]/40">
              Открыть «Мои задачи» →
            </span>
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
